import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import UnitWhiteLabelActivity from "../components/Banking/UnitWhiteLabelActivity";
import PageWrapper from "../components/UI/PageWrapper";
import { useUnitJwt, getCustomerTokenKey, getCustomerTokenExpKey } from "hooks/useUnitJwt";
import { useUser } from "../contexts/UserContext";
import { useArtist } from "../contexts/ArtistContext";
import { getUnitCoAccountDetails } from "../services/api/user.service";

const BankingActivitiesPage: React.FC = () => {
  const { jwt, currentUserId } = useUnitJwt(true); // Always fetch on activities page
  const { userProfile } = useUser();
  const { currentArtistId } = useArtist();
  const [loaded, setLoaded] = useState(false);
  const [accountExists, setAccountExists] = useState<boolean | null>(null); // Track account existence
  const [checkingAccount, setCheckingAccount] = useState(false);
  const lastArtistIdRef = useRef<number | null>(null);
  const hasReloadedRef = useRef<boolean>(false);

  // CRITICAL: Reload page when artist changes to force complete Unit.co widget re-initialization
  // This is necessary because Unit.co widget has internal networking/caching that doesn't reset on component unmount
  useEffect(() => {
    if (currentArtistId && currentArtistId !== lastArtistIdRef.current && lastArtistIdRef.current !== null) {
      console.log('[BankingActivitiesPage] 🔄 Artist changed from', lastArtistIdRef.current, 'to', currentArtistId, '- reloading page to force widget re-initialization');
      
      // Prevent multiple reloads
      if (!hasReloadedRef.current) {
        hasReloadedRef.current = true;
        // Small delay to ensure state is updated
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
      return;
    }
    
    // Initialize on first mount
    if (currentArtistId && lastArtistIdRef.current === null) {
      lastArtistIdRef.current = currentArtistId;
      hasReloadedRef.current = false;
    }
  }, [currentArtistId]);
  
  // CRITICAL: For white label routes, don't read from localStorage
  // Only use tokens from API responses
  const isWhiteLabelRoute = window.location.pathname === '/banking/activities';
  
  // Get account ID from localStorage or state (you may need to fetch this from API)
  // For now, we'll try to get it from localStorage or you can pass it as a prop
  const accountId = localStorage.getItem(`unit_account_id_${currentUserId}`) || null;
  
  const [customerToken, setCustomerToken] = useState<string | undefined>(undefined);
  const [noCustomerToken, setNoCustomerToken] = useState(false);

  // On mount: if valid customer token in localStorage, set state so widget can use it without waiting for event
  useEffect(() => {
    if (!currentUserId || !isWhiteLabelRoute) return;
    const stored = localStorage.getItem(getCustomerTokenKey(currentUserId));
    const storedExp = localStorage.getItem(getCustomerTokenExpKey(currentUserId));
    const bufferMs = 5 * 60 * 1000; // 5 min
    if (stored && stored !== 'undefined' && storedExp) {
      const exp = parseInt(storedExp, 10);
      if (exp > Date.now() + bufferMs) {
        setCustomerToken(stored);
        setNoCustomerToken(false);
      }
    }
  }, [currentUserId, isWhiteLabelRoute]);

  // Listen for customer token ready event (for white label routes - token from API, not localStorage)
  useEffect(() => {
    if (!isWhiteLabelRoute || !currentUserId) return;
    
    const handleCustomerTokenReady = (e: CustomEvent) => {
      if (e.detail?.userId === currentUserId) {
        const token = e.detail?.customerToken;
        if (token && token !== 'undefined') {
          console.log('[BankingActivitiesPage] ✅ Received fresh customer token from API (white label route)');
          setCustomerToken(token);
          setNoCustomerToken(false);
        }
      }
    };
    
    window.addEventListener('unitcoCustomerTokenReady', handleCustomerTokenReady as EventListener);
    
    return () => {
      window.removeEventListener('unitcoCustomerTokenReady', handleCustomerTokenReady as EventListener);
    };
  }, [isWhiteLabelRoute, currentUserId]);

  // When /app returns no token (e.g. user not registered with Unit), show No Bank Accounts
  useEffect(() => {
    if (!isWhiteLabelRoute || !currentUserId) return;
    const handleCustomerTokenError = (e: CustomEvent) => {
      if (e.detail?.userId === currentUserId) {
        const status = e.detail?.status;
        if (status === 404 || status === '404') {
          setNoCustomerToken(true);
        }
      }
    };
    window.addEventListener('unitcoCustomerTokenError', handleCustomerTokenError as EventListener);
    return () => {
      window.removeEventListener('unitcoCustomerTokenError', handleCustomerTokenError as EventListener);
    };
  }, [isWhiteLabelRoute, currentUserId]);

  // Timeout: if account exists but no customerToken after 8s, show No Bank Accounts (avoid endless loading)
  useEffect(() => {
    if (accountExists !== true || customerToken) return;
    const timeoutId = setTimeout(() => {
      setNoCustomerToken(true);
    }, 8000);
    return () => clearTimeout(timeoutId);
  }, [accountExists, customerToken]);

  // Check account existence FIRST before rendering anything
  useEffect(() => {
    if (userProfile?.id && currentArtistId && accountExists === null && !checkingAccount) {
      setCheckingAccount(true);
      const checkAccountExistence = async () => {
        try {
          console.log('🔍 [BankingActivitiesPage] Checking account existence for userId:', userProfile.id, 'artistId:', currentArtistId);
          const accountDetails = await getUnitCoAccountDetails(userProfile.id, currentArtistId);
          console.log('✅ [BankingActivitiesPage] Account check response:', accountDetails);
          console.log('📊 [BankingActivitiesPage] accountExists value:', accountDetails.data?.accountExists, 'type:', typeof accountDetails.data?.accountExists);
          
          // Explicitly set accountExists based on the response - strict boolean check
          const exists = accountDetails.data?.accountExists === true;
          console.log('🔧 [BankingActivitiesPage] Setting accountExists to:', exists);
          setAccountExists(exists);
          
          // If account doesn't exist, log it clearly
          if (!exists) {
            console.log('❌ [BankingActivitiesPage] Account does NOT exist - will NOT render white label');
          }
        } catch (accountCheckError: any) {
          // Handle account check errors
          const errorStatus = accountCheckError?.error?.status || accountCheckError?.status;
          const errorCode = accountCheckError?.error?.code || accountCheckError?.code;
          
          // If 404 or account doesn't exist, set accountExists to false
          if (errorStatus === 404 || errorCode === 'ACCOUNT_NOT_FOUND') {
            console.log('ℹ️ [BankingActivitiesPage] Account not found (404)');
            setAccountExists(false);
          } else {
            // For other errors (403, 500, etc.), assume account might exist but we can't verify
            console.warn('⚠️ [BankingActivitiesPage] Account check failed, assuming account might exist:', accountCheckError);
            setAccountExists(true); // Assume exists to allow rendering
          }
        } finally {
          setCheckingAccount(false);
        }
      };

      checkAccountExistence();
    }
  }, [userProfile?.id, currentArtistId, accountExists, checkingAccount]);

  // Debug log to see current state
  console.log('🎯 [BankingActivitiesPage] Render - accountExists:', accountExists, 'type:', typeof accountExists, 'checkingAccount:', checkingAccount, 'jwt:', !!jwt);

  // STRICT CHECK: Only render white label if accountExists is explicitly true
  const shouldRenderWhiteLabel = accountExists === true;

  const noBankAccountsBlock = (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <p className="text-gray-600 text-lg font-semibold mb-2">No Bank Accounts</p>
        <p className="text-xs text-gray-400 mb-4">Please visit the banking page to create your account</p>
        <Link 
          to="/banking" 
          className="inline-block px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition-colors"
        >
          Go to Banking
        </Link>
      </div>
    </div>
  );

  return (
    <PageWrapper>
      <div className="mx-auto p-12 text-black rounded-lg profile-form flex flex-col gap-[2.875rem] min-h-screen">
        {/* Show loading while checking account */}
        {checkingAccount || (accountExists === null && userProfile?.id && currentArtistId) ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-600 text-sm">Checking account...</p>
            </div>
          </div>
        ) : !shouldRenderWhiteLabel ? (
          noBankAccountsBlock
        ) : shouldRenderWhiteLabel && noCustomerToken && !customerToken ? (
          noBankAccountsBlock
        ) : shouldRenderWhiteLabel && !customerToken ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-600 text-sm">Loading activities…</p>
            </div>
          </div>
        ) : (
          <>
            {!jwt && <p>Loading…</p>}
            {jwt && !loaded && <p>Initializing activities…</p>}
            {jwt && shouldRenderWhiteLabel && (
              <UnitWhiteLabelActivity
                jwtToken={jwt}
                currentUserId={currentUserId}
                accountId={accountId}
                customerToken={customerToken}
                onLoadComplete={() => setLoaded(true)}
              />
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default BankingActivitiesPage;

